/**
 * useFeaturedEvents
 * -----------------
 * Home-page hook that surfaces a short list of upcoming events to feature
 * in the FeaturedEventsCarousel. Featured/sponsored events (which are gated
 * by admin approval in the backend) are preferred; if the result set is
 * thin, we top it up with the next upcoming public events so the carousel
 * stays useful rather than going empty in quiet weeks.
 */
import { useEffect, useState } from "react";
import { listEvents } from "../utils/events";

export function useFeaturedEvents(limit = 6, minFeatured = 3) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Run featured + sponsored pulls in parallel.
        const [featured, sponsored] = await Promise.all([
          listEvents({
            type: "featured",
            upcomingOnly: true,
            statusFilter: "published",
            limit,
          }).catch(() => []),
          listEvents({
            type: "sponsored",
            upcomingOnly: true,
            statusFilter: "published",
            limit,
          }).catch(() => []),
        ]);

        const merged = [];
        const seen = new Set();
        for (const ev of [...featured, ...sponsored]) {
          if (ev && ev.id && !seen.has(ev.id)) {
            seen.add(ev.id);
            merged.push(ev);
          }
        }

        // Top up with general upcoming events if we're short.
        if (merged.length < minFeatured) {
          const filler = await listEvents({
            upcomingOnly: true,
            statusFilter: "published",
            limit,
          }).catch(() => []);
          for (const ev of filler) {
            if (ev && ev.id && !seen.has(ev.id)) {
              seen.add(ev.id);
              merged.push(ev);
              if (merged.length >= limit) break;
            }
          }
        }

        if (!cancelled) setEvents(merged.slice(0, limit));
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [limit, minFeatured]);

  return { events, loading, error };
}

export default useFeaturedEvents;
